import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import apiPaths from "../../../api/endpoints";
import { useRetrieveContent } from "../../hooks/useRetrieveContent";
import AudioPlayer from "../../components/AudioPlayer/AudioPlayer";
import {
  closeIcon,
  jokesBg,
  iKnowImg,
  tellMeImg,
  goodEmoji,
  funnyEmoji,
  hilariousEmoji,
  yetiPreview,
} from "../../appAssets";
import "./Jokes.scss";

interface JokeOption {
  option: string;
  optionValue: string;
  optionAudioUrl?: string;
}

interface JokeData {
  _id: string;
  documentId: string;
  audioUrl?: string;
  imageUrl?: string;
  options: JokeOption[];
  question: string;
  type: string;
}

const EMOJI_REACTIONS = [
  { id: "Good", label: "Good", icon: goodEmoji },
  { id: "Funny", label: "Funny", icon: funnyEmoji },
  { id: "Hilarious", label: "Hilarious", icon: hilariousEmoji },
];

function Jokes() {
  const navigate = useNavigate();
  const { getRetrieveContent, postRetrieveContent } = useRetrieveContent();

  const [loading, setLoading] = useState(false);
  const [jokeData, setJokeData] = useState<JokeData | null>(null);
  const [isRevealed, setIsRevealed] = useState(false);
  const [selectedReaction, setSelectedReaction] = useState<string | null>(
    null,
  );

  const fetchJoke = useCallback(async () => {
    setLoading(true);
    const response = await getRetrieveContent(apiPaths.RETRIEVE_JOKE);
    setJokeData(response?.data ?? null);
    setLoading(false);
  }, [getRetrieveContent]);

  useEffect(() => {
    fetchJoke();
  }, [fetchJoke]);

  const handleClose = () => navigate(-1);

  const handleReveal = () => setIsRevealed(true);

  const answer = jokeData?.options?.[0];

  const handleReaction = async (reaction: (typeof EMOJI_REACTIONS)[number]) => {
    setSelectedReaction(reaction.id);
    if (!jokeData?.documentId) return;
    setLoading(true);
    const formdata = new FormData();
    formdata.append("contentId", jokeData.documentId);
    formdata.append("answer", reaction.label);
    formdata.append("contentType", "Jokes");
    await postRetrieveContent(formdata);
    setLoading(false);
    navigate(-1);
  };

  return (
    <div className="jokes">
      {!isRevealed && <img src={jokesBg} alt="" className="jokes__bg" />}

      <div className={`jokes__card${!isRevealed ? " jokes__card--transparent" : ""}`}>
        <div className="jokes__header">
          <div className="jokes__header-spacer" />
          <h2 className="jokes__title">Let&rsquo;s hear a joke</h2>
          <button
            type="button"
            className="jokes__close"
            onClick={handleClose}
            aria-label="Close"
          >
            <img src={closeIcon} alt="" className="jokes__close-icon" />
          </button>
        </div>

        {!isRevealed ? (
          <div className="jokes__question">
            <p className="jokes__question-text">{jokeData?.question}</p>
            <div className="jokes__options">
              <button
                type="button"
                className="jokes__option-btn"
                onClick={handleReveal}
              >
                <img src={iKnowImg} alt="" className="jokes__option-img" />
                <span>I know!</span>
              </button>
              <button
                type="button"
                className="jokes__option-btn"
                onClick={handleReveal}
              >
                <img src={tellMeImg} alt="" className="jokes__option-img" />
                <span>Tell me</span>
              </button>
            </div>
            {jokeData?.audioUrl && (
              <AudioPlayer key={jokeData.audioUrl} uri={jokeData.audioUrl} autoPlay />
            )}
          </div>
        ) : (
          <div className="jokes__answer">
            <div className="jokes__answer-content">
              <p className="jokes__answer-text">{answer?.option}</p>
              {jokeData?.imageUrl && (
                <img
                  src={jokeData.imageUrl}
                  alt=""
                  className="jokes__answer-img"
                />
              )}
            </div>
            <div className="jokes__emoji-row">
              {EMOJI_REACTIONS.map((reaction) => (
                <button
                  key={reaction.id}
                  type="button"
                  className={`jokes__emoji-btn${
                    selectedReaction === reaction.id
                      ? " jokes__emoji-btn--selected"
                      : ""
                  }`}
                  onClick={() => handleReaction(reaction)}
                  aria-label={reaction.label}
                >
                  <img
                    src={reaction.icon}
                    alt=""
                    className="jokes__emoji-icon"
                  />
                </button>
              ))}
            </div>
            {answer?.optionAudioUrl && (
              <AudioPlayer
                key={answer.optionAudioUrl}
                uri={answer.optionAudioUrl}
                autoPlay
              />
            )}
          </div>
        )}

        {!isRevealed && (
          <img src={yetiPreview} alt="" className="jokes__yeti" />
        )}
      </div>

      {loading && <div className="jokes__spinner" />}
    </div>
  );
}

export default Jokes;
